import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { API, useAuth } from "@/App";
import EhiLogOutcomeModal from "@/components/ehi/EhiLogOutcomeModal";
import { ClientQueuePanel, ClientCallStage, ClientRecentActivity } from "@/components/clientdialer/ClientLiveParts";

const LIVE = ["dialing", "ringing", "connected"];
const control = (action) => window.dispatchEvent(new CustomEvent("webphone:control", { detail: { action } }));

function Head({ name, onBack }) {
  return (
    <div style={{ padding: "20px 28px 16px", borderBottom: "0.5px solid var(--border-soft)" }}>
      <button data-testid="cld-exit" onClick={onBack} className="btn" style={{ fontSize: 11, padding: "4px 10px", display: "inline-flex", gap: 4, alignItems: "center", marginBottom: 6 }}>
        <ArrowLeft style={{ width: 12, height: 12 }} /> Back
      </button>
      <h1 data-testid="cld-title" style={{ fontSize: 26, fontWeight: 700, color: "var(--navy)", letterSpacing: "-0.02em", margin: 0 }}>Clients Dialer</h1>
      <div style={{ fontSize: 12, color: "var(--text-3)" }}>{name || "Client campaign"} · live calling</div>
    </div>
  );
}

/** Full-page live dialer for one client campaign — same flow as /dialer/live, on EHI clients. */
export default function ClientLiveDialer() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const campaignId = params.get("campaign_id");
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [q, setQ] = useState({ loading: true, upcoming: [] });
  const [campName, setCampName] = useState("");
  const [phone, setPhone] = useState("");
  const [state, setState] = useState("idle");
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [timer, setTimer] = useState(0);
  const [notes, setNotes] = useState("");
  const [callMeta, setCallMeta] = useState(null);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activity, setActivity] = useState({ outcomes: [], calls: [] });
  const pending = useRef(null);
  const savedOutcome = useRef(null);
  const client = q.client;

  const loadNext = useCallback(async () => {
    setQ((x) => ({ ...x, loading: true })); setState("idle"); setTimer(0); setNotes(""); setCallMeta(null); setOutcomeOpen(false);
    try {
      const r = await axios.get(`${API}/client-dialer/campaigns/${campaignId}/next`, h);
      setQ({ loading: false, upcoming: [], ...r.data });
      const c = r.data.client;
      setPhone(c?.phones?.[0]?.number || "");
      if (c) {
        const [o, hc] = await Promise.all([
          axios.get(`${API}/ehi-clients/${c.client_id}/outcomes`, { ...h, params: { limit: 5 } }),
          axios.get(`${API}/client-dialer/clients/${c.client_id}/history`, h)]);
        setActivity({ outcomes: o.data.outcomes || [], calls: hc.data.calls || [] });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load the next client"); setQ({ loading: false, client: null, upcoming: [] }); }
  }, [campaignId, token]); // eslint-disable-line react-hooks/exhaustive-deps -- h derives from token
  useEffect(() => { loadNext(); }, [loadNext]);
  useEffect(() => {
    axios.get(`${API}/client-dialer/campaigns`, { ...h, params: { include_archived: true } })
      .then((r) => setCampName((r.data.campaigns || []).find((c) => c.id === campaignId)?.name || "")).catch(() => {});
  }, [campaignId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onState = (e) => {
      const s = e.detail?.state;
      if (!pending.current && !["dialing", "ringing", "connected", "held"].includes(state)) return;
      if (LIVE.includes(s)) {
        if (pending.current) { clearTimeout(pending.current.timer); pending.current = null; }
        setState(e.detail?.held ? "held" : s); setMuted(!!e.detail?.muted); setHeld(!!e.detail?.held);
      }
    };
    const onEnd = (e) => {
      if (!client || e.detail?.lead_id !== `ehi-${client.client_id}`) return;
      setState("idle"); setMuted(false); setHeld(false);
      setCallMeta({ phone, duration_sec: e.detail?.duration_sec, external_id: e.detail?.external_id, activity_id: e.detail?.activity_id });
      setOutcomeOpen(true);
    };
    window.addEventListener("webphone:state", onState);
    window.addEventListener("webphone:call-ended", onEnd);
    return () => { window.removeEventListener("webphone:state", onState); window.removeEventListener("webphone:call-ended", onEnd); };
  }, [client, phone, state]);
  useEffect(() => {
    if (state !== "connected" && state !== "held") return undefined;
    const t = setInterval(() => setTimer((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [state]);
  useEffect(() => () => pending.current && clearTimeout(pending.current.timer), []);

  const call = () => {
    setState("starting"); setTimer(0);
    const timer = setTimeout(() => {
      pending.current = null; setState("idle");
      toast.error("The call didn't start. Check that RingCentral is connected in Settings, then try again.");
    }, 8000);
    pending.current = { timer };
    window.dispatchEvent(new CustomEvent("webphone:dial", { detail: { number: phone, leadId: `ehi-${client.client_id}`, leadName: client.clinic } }));
  };
  const skip = async () => {
    await axios.post(`${API}/client-dialer/campaigns/${campaignId}/skip`, { client_id: client.client_id }, h).catch(() => {});
    loadNext();
  };
  const dnc = async () => {
    if (!window.confirm(`Mark ${client.clinic} as Do Not Call? It leaves every client campaign.`)) return;
    try { await axios.post(`${API}/client-dialer/dnc`, { client_id: client.client_id }, h); toast.success("Marked Do Not Call"); loadNext(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't mark Do Not Call"); }
  };
  const saveOutcome = async (d) => {
    setSaving(true);
    try {
      let outcome = savedOutcome.current;
      if (!outcome || outcome.client_id !== client.client_id) {
        const o = await axios.post(`${API}/ehi-clients/${client.client_id}/outcomes`, { ...d, campaign_id: campaignId }, h);
        outcome = o.data.outcome; savedOutcome.current = outcome;
      }
      await axios.post(`${API}/client-dialer/calls`, {
        client_id: client.client_id, campaign_id: campaignId, outcome_id: outcome.id,
        phone: callMeta?.phone || phone, duration_sec: callMeta?.duration_sec || 0,
        external_id: callMeta?.external_id, activity_id: callMeta?.activity_id,
      }, h);
      savedOutcome.current = null;
      toast.success(outcome.task_id ? "Outcome saved — follow-up task added" : "Outcome saved");
      loadNext();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Couldn't save the outcome");
    }
    setSaving(false);
  };

  const mustLog = !!callMeta;
  const live = ["dialing", "ringing", "connected", "held"].includes(state);
  return (
    <div data-testid="client-live-dialer" style={{ height: "100vh", display: "flex", flexDirection: "column", background: "var(--page-bg)" }}>
      <Head name={campName} onBack={() => navigate("/clients-dialer")} />
      {!client ? (
        <div data-testid="cld-queue-empty" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, color: "var(--text-3)" }}>
          {q.loading ? (<>
            <div style={{ width: 32, height: 32, borderRadius: "50%", border: "2.5px solid var(--border-soft)", borderTopColor: "var(--blue)", animation: "spin 1s linear infinite" }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>Loading queue…</div>
          </>) : (<>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>No clients left in this campaign</div>
            <button className="btn" data-testid="cld-back-empty" onClick={() => navigate("/clients-dialer")}>Back to Clients Dialer</button>
          </>)}
        </div>
      ) : (
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "320px 1fr 360px", gap: 18, padding: 22, overflow: "auto", opacity: q.loading ? 0.6 : 1 }}>
          <ClientQueuePanel current={client} upcoming={q.upcoming || []} remaining={q.remaining} onSkip={skip} skipDisabled={mustLog || live} />
          <ClientCallStage client={client} callback={q.callback} phone={phone} setPhone={setPhone} state={state} timer={timer}
            muted={muted} held={held} onCall={call} onHangup={() => control("hangup")} onMute={() => control("mute")} onHold={() => control("hold")}
            notes={notes} setNotes={setNotes} onLogOutcome={() => setOutcomeOpen(true)} onDnc={dnc} mustLog={mustLog} />
          <ClientRecentActivity outcomes={activity.outcomes} calls={activity.calls} />
        </div>
      )}
      {outcomeOpen && client && (
        <EhiLogOutcomeModal key={`${client.client_id}-${callMeta ? "call" : "manual"}`} initialNotes={notes}
          client={{ client_id: client.client_id, clinic: client.clinic, phone: client.phones?.[0]?.number, email: client.email }}
          clientName={client.clinic} clientId={client.client_id} saving={saving} onSave={saveOutcome}
          onClose={() => { setOutcomeOpen(false); if (mustLog) toast.info("Log this call's outcome before moving to the next client"); }} />
      )}
    </div>
  );
}
